import { zodResolver } from "@hookform/resolvers/zod";
import {
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import BackButton from "@/components/back-button";
import Button from "@/components/button";
import { AppErrorBoundary } from "@/components/ErrorBoundary";
import { Combobox } from "@/components/combobox";
import Input from "@/components/input";
import { PlacesAutocomplete } from "@/components/places-autocomplete";
import Textarea from "@/components/textarea";
import { TimeOfDayPicker } from "@/components/time-of-day-picker";
import { getShiftDetail } from "@/queries/api/employerDashboardApi";
import type { ShiftDetail } from "@/queries/api/employerDashboardApi";
import { useUpdateShiftMutation } from "@/queries/hooks/shiftHooks";
import { QueryKeys } from "@/queries/queryKeys";
import { notify } from "@/utils";
import { useRole } from "@/utils/hooks/use-role";
import { Logger } from "@/utils/logger";
import { useOrderedRoleOptions } from "@/utils/hooks/use-ordered-role-options";
import { shiftHasStarted } from "@/utils/shift-card";
import { Loader2 } from "lucide-react";

const TIME_RE = /^\d{2}:\d{2}$/;

const logger = Logger.scope("Shift Edit");

function makeEmployerShiftDetailQuery(shiftId: number) {
  return {
    queryKey: [QueryKeys.UPCOMING_SHIFTS, "detail", shiftId],
    queryFn: () => getShiftDetail(shiftId),
  };
}

/** "HH:mm:ss" (or "HH:mm") -> "HH:MM" for the time picker. */
function toHHMM(t?: string | null): string {
  if (!t) return "";
  return t.slice(0, 5);
}

type EditFormValues = {
  location: string;
  place: any;
  role: string;
  wage: number;
  desc: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
};

export const Route = createFileRoute("/app/$role/_app/shifts/$shiftId/edit/")({
  beforeLoad: ({ params }) => {
    if (params.role !== "employer") {
      throw redirect({
        to: "/app/$role/dashboard",
        params: { role: params.role },
      });
    }
  },
  loader: ({ context, params }) => {
    const shiftId = Number(params.shiftId);
    if (Number.isFinite(shiftId)) {
      context.queryClient.ensureQueryData(
        makeEmployerShiftDetailQuery(shiftId),
      );
    }
  },
  component: EditShiftPage,
  staticData: {
    layout: {
      bg: "white",
      boxed: true,
    },
  },
});

function EditShiftPage() {
  const { t } = useTranslation("shifts");
  const { shiftId } = Route.useParams();
  const role = useRole();
  const navigate = useNavigate();
  const updateShift = useUpdateShiftMutation();
  const roles = useOrderedRoleOptions();

  const shiftIdNum = Number(shiftId);
  const { data: detail } = useSuspenseQuery<ShiftDetail>(
    makeEmployerShiftDetailQuery(shiftIdNum),
  );

  const EditShiftSchema = useMemo(
    () =>
      z
        .object({
          location: z.string(),
          place: z.any(),
          role: z.string().min(1, { message: t("create.errors.roleRequired") }),
          wage: z.coerce
            .number()
            .gt(0, { message: t("create.errors.wageInvalid") }),
          desc: z.string().default(""),
          startDate: z
            .string()
            .min(1, { message: t("create.errors.datesRequired") }),
          endDate: z
            .string()
            .min(1, { message: t("create.errors.datesRequired") }),
          startTime: z
            .string()
            .regex(TIME_RE, { message: t("create.errors.startTimeRequired") }),
          endTime: z
            .string()
            .regex(TIME_RE, { message: t("create.errors.endTimeRequired") }),
        })
        .superRefine((v, ctx) => {
          if (v.endDate && v.startDate && v.endDate < v.startDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["endDate"],
              message: t("edit.errors.endDateAfterStart"),
            });
          }
          // Only enforce end-after-start when the shift is a single day.
          if (
            v.startDate &&
            v.endDate &&
            v.startDate === v.endDate &&
            TIME_RE.test(v.startTime) &&
            TIME_RE.test(v.endTime) &&
            v.endTime <= v.startTime
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["endTime"],
              message: t("create.errors.endTimeAfterStart"),
            });
          }
        }),
    [t],
  );

  const initialStartDate = detail.start_date ?? "";
  const initialPlace = useMemo(() => {
    const addr = detail.address;
    if (addr?.latitude != null && addr.longitude != null) {
      return {
        formattedAddress: addr.address1 ?? addr.location ?? "",
        name: addr.location ?? "",
        state: addr.state ?? undefined,
        zipCode: addr.zipcode ?? undefined,
        location: { lat: addr.latitude, lng: addr.longitude },
      };
    }
    return null;
  }, [detail]);

  const form = useForm<EditFormValues>({
    resolver: zodResolver(EditShiftSchema) as any,
    mode: "onSubmit",
    defaultValues: {
      location:
        detail.address?.address1 ??
        detail.address?.location ??
        detail.location ??
        "",
      place: initialPlace,
      role: detail.role_name ?? "",
      wage:
        typeof detail.wage === "number"
          ? detail.wage
          : detail.wage != null
            ? Number(detail.wage)
            : 0,
      desc: detail.role_description ?? "",
      startDate: detail.start_date ?? "",
      endDate: detail.end_date ?? detail.start_date ?? "",
      startTime: toHHMM(detail.start_time),
      endTime: toHHMM(detail.end_time),
    },
  });

  function parsePlaceToAddress(place: any): Record<string, any> | null {
    if (!place || !place.location?.lat || !place.location?.lng) return null;
    const obj = {
      state: place.state,
      address1: place.formattedAddress,
      zipcode: place.zipCode,
      latitude: place.location?.lat,
      longitude: place.location?.lng,
      location: place.name,
    };
    return Object.fromEntries(
      Object.entries(obj).filter(([, v]) => v != null && v !== ""),
    );
  }

  async function onSubmit(values: EditFormValues) {
    // Only send the address when the employer picked a new place; otherwise the
    // backend keeps the existing location.
    const address =
      values.place && values.place !== initialPlace
        ? parsePlaceToAddress(values.place)
        : null;

    const payload: Record<string, unknown> = {
      role_name: values.role,
      role_description: values.desc || undefined,
      wage: typeof values.wage === "number" ? values.wage : Number(values.wage),
      start_time: `${values.startTime}:00`,
      end_time: `${values.endTime}:00`,
    };
    if (address) payload.address = address;
    // Only send the date when it moved; the backend keeps the shift single-day.
    if (values.startDate !== initialStartDate) {
      payload.start_date = values.startDate;
      payload.end_date = values.startDate;
    }

    try {
      await updateShift.mutateAsync({ shiftId: shiftIdNum, shift: payload });
      notify.success(t("edit.shiftUpdated"));
      navigate({ to: "/app/$role/shifts", params: { role: String(role) } });
    } catch (err) {
      notify.error(err instanceof Error ? err.message : String(err));
    }
  }

  function onError(errors: Record<string, unknown>) {
    logger.error("Edit form validation errors:", errors);
    notify.error(t("create.fillRequired"));
  }

  // A shift can only be edited before it starts. Guard direct navigation here;
  // the backend enforces the same rule authoritatively.
  const started = shiftHasStarted(
    detail.start_date
      ? `${detail.start_date}T${detail.start_time ?? "00:00:00"}`
      : null,
  );
  if (started) {
    return (
      <section className="w-full max-w-full px-4 py-4 mx-auto sm:max-w-[655px] sm:py-12">
        <div className="w-full flex flex-row items-center justify-start mb-6">
          <BackButton />
          <h1 className="heading-2 text-center w-full text-lg sm:text-2xl">
            {t("edit.title")}
          </h1>
        </div>
        <p className="body text-garnet/80 text-center">
          {t("edit.startedNotice")}
        </p>
      </section>
    );
  }

  return (
    <section className="w-full max-w-full px-4 py-4 mx-auto sm:max-w-[655px] sm:py-12 pb-36 md:pb-28">
      <div className="w-full flex flex-row items-center justify-start mb-6 sm:mb-[43px]">
        <BackButton />
        <h1 className="heading-2 text-center w-full text-lg sm:text-2xl">
          {t("edit.title")}
        </h1>
      </div>
      <form
        className="grid gap-4 w-full"
        onSubmit={form.handleSubmit(onSubmit, onError)}
      >
        <fieldset className="w-full">
          {/* Location with Google Autocomplete */}
          <Controller
            name="location"
            control={form.control}
            render={({ field, fieldState }) => (
              <AppErrorBoundary
                fallback={
                  <Input
                    label={t("create.location")}
                    placeholder={t("create.locationPlaceholder")}
                    value={field.value}
                    onChange={field.onChange}
                    state={fieldState.error ? "error" : "default"}
                    hint={fieldState.error?.message}
                  />
                }
              >
                <PlacesAutocomplete
                  label={t("create.location")}
                  value={field.value}
                  onPlaceSelect={(place) => {
                    if (place) {
                      field.onChange(place?.formattedAddress ?? "");
                      form.setValue("place", place ?? {});
                    }
                  }}
                  state={fieldState.error ? "error" : "default"}
                  hint={fieldState.error?.message}
                  onChange={field.onChange}
                />
              </AppErrorBoundary>
            )}
          />
          {/* Role + Wage */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="role"
              control={form.control}
              render={({ field, fieldState }) => (
                <Combobox
                  label={t("create.requiredRole")}
                  options={roles}
                  value={typeof field.value === "string" ? field.value : ""}
                  onChange={(v) =>
                    field.onChange(typeof v === "string" ? v : "")
                  }
                  state={fieldState.error ? "error" : "default"}
                  hint={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="wage"
              control={form.control}
              render={({ field, fieldState }) => (
                <Input
                  label={t("create.wage")}
                  type="number"
                  value={String(field.value)}
                  onChange={(e) => field.onChange(Number(e.currentTarget.value))}
                  startAdornment={<span className="text-garnet/70">$</span>}
                  endAdornment={<span className="text-garnet/70">/hr</span>}
                  state={fieldState.error ? "error" : "default"}
                  hint={fieldState.error?.message}
                />
              )}
            />
          </div>
          <Controller
            name="desc"
            control={form.control}
            render={({ field, fieldState }) => (
              <Textarea
                value={field.value}
                onChange={(e) => field.onChange(e)}
                label={t("create.roleDescription")}
                placeholder={t("create.roleDescriptionPlaceholder")}
                rows={4}
                className="min-h-[96px]"
                state={fieldState.error ? "error" : "default"}
                hint={fieldState.error?.message}
              />
            )}
          />

          {/* Date — a shift is a single day (other days of a series are their own shifts) */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="startDate"
              control={form.control}
              render={({ field, fieldState }) => (
                <Input
                  label={t("edit.date")}
                  type="date"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.currentTarget.value);
                    form.setValue("endDate", e.currentTarget.value);
                  }}
                  state={fieldState.error ? "error" : "default"}
                  hint={fieldState.error?.message}
                />
              )}
            />
          </div>

          {/* Times */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="startTime"
              control={form.control}
              render={({ field, fieldState }) => (
                <TimeOfDayPicker
                  label={t("create.from")}
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="endTime"
              control={form.control}
              render={({ field, fieldState }) => (
                <TimeOfDayPicker
                  label={t("create.to")}
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>

          {/* Submit */}
          <div className="pt-4 sm:pt-2">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full sm:w-[320px]"
              disabled={updateShift.isPending}
            >
              {updateShift.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {updateShift.isPending
                ? t("edit.saving")
                : t("edit.saveChanges")}
            </Button>
          </div>
        </fieldset>
      </form>
    </section>
  );
}
